import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const DEFAULT_TEMPLATES = {
  'Toddler Environment (NCF Aligned)': {
    'Fine Motor & Coordination': {
      'Hand Strength & Pincer Grip': '',
      'Pencil Control & Pre-writing': '',
      'Scissor Skills & Cutting': '',
      'Hand-Eye Coordination': ''
    },
    'Gross Motor Development': {
      'Balance & Walking Line': '',
      'Jumping & Hopping': '',
      'Climbing & Coordination': '',
      'Spatial Body Awareness': ''
    },
    'Cognitive & Problem Solving': {
      'Shape & Color Matching': '',
      'Puzzles & Block Building': '',
      'Sequence & Memory Games': ''
    },
    'Language Development': {
      'Vocabulary Expansion': '',
      'Listening & Comprehension': '',
      'Expressive Speech & Sentences': ''
    },
    'Sensorial Exploration': {
      'Tactile Texture Discrimination': '',
      'Visual Color Tablets': '',
      'Auditory Sound Matching': ''
    },
    'Social & Self Care': {
      'Independent Hand Washing': '',
      'Sharing & Turn Taking': '',
      'Following Classroom Routines': '',
      'Care of Personal Belongings': ''
    }
  },
  'Baby Environment (Milestone Tracking)': {
    'Developmental Milestones': {
      'Responds to familiar voices and sounds': '',
      'Responds to own name': '',
      'Uses gestures (pointing, waving)': '',
      'Uses words or sounds to communicate': '',
      'Explores objects independently': '',
      'Demonstrates curiosity about the environment': '',
      'Shows understanding of cause and effect': '',
      'Sits independently': '',
      'Crawls or scoots': '',
      'Pulls to stand': '',
      'Walks independently': '',
      'Grasps and manipulates objects': '',
      'Uses pincer grip': '',
      'Self-feeds with assistance or independently': '',
      'Shows attachment to familiar caregivers': '',
      'Interacts with peers and adults': '',
      'Expresses a range of emotions': ''
    },
    'Social & Emotional Well-being': {
      'Makes eye contact': '',
      'Responds to caregivers': '',
      'Self-soothing abilities': ''
    }
  },
  'Pre-Primary (CAPS Foundation Phase)': {
    'Home Language & Literacy': {
      'Phonetic Sounds Recognition': '',
      'Letter Formation & Emergent Writing': '',
      'Vocabulary & Oral Expression': '',
      'Comprehension & Story Telling': ''
    },
    'Mathematics & Early Numeracy': {
      'Number Counting (1-20)': '',
      'Quantity & Number Symbol Matching': '',
      'Spatial Shapes & Geometry': '',
      'Patterns & Measurement Concepts': ''
    },
    'Life Skills & Physical Development': {
      'Personal Hygiene & Self-Care': '',
      'Gross Motor & Physical Movement': '',
      'Creative Arts & Music Expression': '',
      'Social Well-being & Respect': ''
    }
  },
  'Montessori Casa (SAMA Aligned)': {
    'Practical Life': {
      'Pouring & Spooning Exercises': '',
      'Dressing Frames & Buttons': '',
      'Care of Environment (Dusting/Washing)': '',
      'Grace & Courtesy Manners': ''
    },
    'Sensorial Material Work': {
      'Pink Tower & Broad Stairs': '',
      'Cylinder Blocks': '',
      'Color Tablets Box 2 & 3': '',
      'Geometric Cabinet': ''
    },
    'Montessori Language': {
      'Sandpaper Letters Trace': '',
      'Movable Alphabet Word Building': '',
      'Oral Language & Classified Cards': ''
    },
    'Montessori Mathematics': {
      'Number Rods 1-10': '',
      'Spindle Boxes': '',
      'Golden Beads Decimal Intro': ''
    },
    'Cultural & Nature Studies': {
      'Continent Puzzle Maps': '',
      'Botany & Animal Puzzle Cards': '',
      'Art & Cultural Song': ''
    }
  }
};

DEFAULT_TEMPLATES['Toddler Environment'] = DEFAULT_TEMPLATES['Toddler Environment (NCF Aligned)'];
DEFAULT_TEMPLATES['Baby Environment'] = DEFAULT_TEMPLATES['Baby Environment (Milestone Tracking)'];
DEFAULT_TEMPLATES['Pre-Primary Environment'] = DEFAULT_TEMPLATES['Pre-Primary (CAPS Foundation Phase)'];
DEFAULT_TEMPLATES['Pre-Primary'] = DEFAULT_TEMPLATES['Pre-Primary (CAPS Foundation Phase)'];
DEFAULT_TEMPLATES['Montessori Casa'] = DEFAULT_TEMPLATES['Montessori Casa (SAMA Aligned)'];
DEFAULT_TEMPLATES['Custom'] = {};

const getTemplateByType = (type) => {
  if (!type) return DEFAULT_TEMPLATES['Baby Environment (Milestone Tracking)'];
  if (DEFAULT_TEMPLATES[type]) return DEFAULT_TEMPLATES[type];
  const norm = String(type).toLowerCase();
  if (norm.includes('baby')) return DEFAULT_TEMPLATES['Baby Environment (Milestone Tracking)'];
  if (norm.includes('toddler')) return DEFAULT_TEMPLATES['Toddler Environment (NCF Aligned)'];
  if (norm.includes('pre-primary') || norm.includes('pre primary')) return DEFAULT_TEMPLATES['Pre-Primary (CAPS Foundation Phase)'];
  if (norm.includes('montessori') || norm.includes('casa')) return DEFAULT_TEMPLATES['Montessori Casa (SAMA Aligned)'];
  return DEFAULT_TEMPLATES['Baby Environment (Milestone Tracking)'];
};

const currentYr = new Date().getFullYear();
const currentSessionYear = `${currentYr}-${String(currentYr + 1).slice(-2)}`;

const emptyReportForm = {
  student_id: '',
  report_type: 'Baby Environment',
  academic_year: currentSessionYear,
  term: 'Term 1',
  age: '3',
  status: 'draft',
  strengths: '',
  goals: '',
  teacher_comment: '',
  assessments: DEFAULT_TEMPLATES['Baby Environment']
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentBranch, user } = useAuth();
  
  const [reports, setReports] = useState([]);
  const [branches, setBranches] = useState([]);
  const [classesList, setClassesList] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [filters, setFilters] = useState({ branch_id: currentBranch?.branch_id || '', class_id: '', academic_year: '' });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // Modal states
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | null
  const [editingReportId, setEditingReportId] = useState(null);
  const [form, setForm] = useState(emptyReportForm);
  const [saving, setSaving] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Delete Confirm Modal
  const [deletingReport, setDeletingReport] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  // New assessment category input
  const [newCatName, setNewCatName] = useState('');

  // Allowed branches for user
  const userRole = user?.role;
  const isCenterManager = userRole === 'center_manager';

  const allowedBranches = isCenterManager
    ? (user?.assigned_branches && user.assigned_branches.length > 0
        ? user.assigned_branches.map(b => ({ id: b.branch_id, name: b.name }))
        : (currentBranch ? [{ id: currentBranch.branch_id, name: currentBranch.name }] : []))
    : branches;

  useEffect(() => {
    async function loadBranches() {
      try {
        const response = await api.get('/admin/branches');
        setBranches(response.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load branches');
      }
    }
    loadBranches();
  }, []);

  useEffect(() => {
    async function loadClasses() {
      const targetBranchId = filters.branch_id || currentBranch?.branch_id;
      if (!targetBranchId) {
        setClassesList([]);
        return;
      }
      try {
        const response = await api.get(`/branches/${targetBranchId}/classes`);
        const list = response.data.data || response.data.classes || [];
        setClassesList(list.filter(c => c.is_active === 1 || c.is_active === undefined));
      } catch (err) {
        setClassesList([]);
      }
    }
    loadClasses();
  }, [filters.branch_id, currentBranch?.branch_id]);

  useEffect(() => {
    async function loadStudents() {
      try {
        const response = await api.get('/students');
        setAllStudents(response.data.data || []);
      } catch (err) {
        console.error('Failed to load students', err);
      }
    }
    loadStudents();
  }, []);

  useEffect(() => {
    if (currentBranch) {
      setFilters(prev => ({ ...prev, branch_id: currentBranch.branch_id, class_id: '' }));
    }
  }, [currentBranch]);

  useEffect(() => {
    loadReports();
  }, [filters.branch_id, filters.class_id, filters.academic_year]);

  async function loadReports() {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.class_id) params.class_id = filters.class_id;
      if (filters.academic_year) params.academic_year = filters.academic_year;
      const response = await api.get('/admin/student-reports', { params });
      setReports(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load reports');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && reports.length > 0) {
      const rep = reports.find(r => String(r.id) === String(editId));
      if (rep) {
        openEditModal(rep);
        setSearchParams({}, { replace: true });
      }
    }
  }, [reports, searchParams]);

  const handleQuickPublishToggle = async (report) => {
    const isCurrentlyPublished = report.status === 'published';
    const newStatus = isCurrentlyPublished ? 'draft' : 'published';
    setPublishingId(report.id);
    setError('');
    try {
      if (newStatus === 'published') {
        await api.put(`/admin/student-reports/${report.id}/publish`);
      } else {
        await api.put(`/admin/student-reports/${report.id}`, {
          ...report,
          status: 'draft'
        });
      }
      setSuccessMsg(`Report #${report.id} status changed to ${newStatus.toUpperCase()}`);
      setTimeout(() => setSuccessMsg(''), 3000);
      await loadReports();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update report status');
    } finally {
      setPublishingId(null);
    }
  };

  // Filter students based on branch access
  const availableStudents = allStudents.filter(s => {
    if (isCenterManager) {
      const allowedIds = allowedBranches.map(b => Number(b.id));
      return allowedIds.includes(Number(s.branch_id));
    }
    if (filters.branch_id) {
      return Number(s.branch_id) === Number(filters.branch_id);
    }
    return true;
  });

  const openAddModal = () => {
    setEditingReportId(null);
    const initialStudentId = availableStudents.length > 0 ? String(availableStudents[0].id) : '';
    const baseTemplate = getTemplateByType('Baby Environment');
    setForm({
      ...emptyReportForm,
      student_id: initialStudentId,
      assessments: JSON.parse(JSON.stringify(baseTemplate || {}))
    });
    setCollapsedCategories({});
    setError('');
    setModalMode('add');
  };

  const openEditModal = (report) => {
    setEditingReportId(report.id);
    let parsedAssessments = report.assessments || {};
    if (typeof parsedAssessments === 'string') {
      try { parsedAssessments = JSON.parse(parsedAssessments); } catch (e) { parsedAssessments = {}; }
    }

    const reportType = report.report_type || 'Baby Environment';
    const baseTemplate = getTemplateByType(reportType);
    const mergedAssessments = JSON.parse(JSON.stringify(baseTemplate || {}));

    if (parsedAssessments && typeof parsedAssessments === 'object') {
      Object.keys(parsedAssessments).forEach(cat => {
        if (!mergedAssessments[cat]) {
          mergedAssessments[cat] = {};
        }
        if (typeof parsedAssessments[cat] === 'object' && parsedAssessments[cat] !== null) {
          Object.assign(mergedAssessments[cat], parsedAssessments[cat]);
        }
      });
    }

    setForm({
      student_id: String(report.student_id || ''),
      report_type: reportType,
      academic_year: report.academic_year || '2024-25',
      term: report.term || 'Term 1',
      age: report.age ? String(report.age) : '5',
      status: report.status || 'draft',
      strengths: report.strengths || '',
      goals: report.goals || '',
      teacher_comment: report.teacher_comment || '',
      assessments: mergedAssessments
    });
    setCollapsedCategories({});
    setError('');
    setModalMode('edit');
  };

  const handleReportTypeChange = (newType) => {
    setForm(prev => {
      const updated = { ...prev, report_type: newType };
      const tmpl = getTemplateByType(newType);
      updated.assessments = JSON.parse(JSON.stringify(tmpl || {}));
      return updated;
    });
  };

  const handleAssessmentStatusChange = (category, itemKey, newStatus) => {
    setForm(prev => {
      const assessments = { ...prev.assessments };
      if (!assessments[category]) assessments[category] = {};
      const currentVal = assessments[category][itemKey];
      const updatedVal = currentVal === newStatus ? '' : newStatus;
      assessments[category] = { ...assessments[category], [itemKey]: updatedVal };
      return { ...prev, assessments };
    });
  };

  const toggleCategoryCollapse = (catName) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [catName]: !prev[catName]
    }));
  };

  const handleAddMilestoneItem = (category, e) => {
    if (e) e.stopPropagation();
    const itemName = window.prompt(`Enter new milestone item for category "${category}":`);
    if (!itemName || !itemName.trim()) return;
    setForm(prev => {
      const assessments = { ...prev.assessments };
      if (!assessments[category]) assessments[category] = {};
      assessments[category] = { ...assessments[category], [itemName.trim()]: 'Developing' };
      return { ...prev, assessments };
    });
  };

  const handleDeleteMilestoneItem = (category, itemKey) => {
    setForm(prev => {
      const assessments = { ...prev.assessments };
      if (assessments[category]) {
        const catCopy = { ...assessments[category] };
        delete catCopy[itemKey];
        assessments[category] = catCopy;
      }
      return { ...prev, assessments };
    });
  };

  const handleAddCategory = () => {
    if (!newCatName.trim()) return;
    const cat = newCatName.trim();
    setForm(prev => {
      const assessments = { ...prev.assessments };
      if (!assessments[cat]) assessments[cat] = {};
      return { ...prev, assessments };
    });
    setNewCatName('');
  };

  const handleDeleteCategory = (category, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Delete section "${category}" and all its milestone items?`)) return;
    setForm(prev => {
      const assessments = { ...prev.assessments };
      delete assessments[category];
      return { ...prev, assessments };
    });
  };

  const handleSaveReport = async (targetStatus) => {
    if (!form.student_id) {
      setError('Please select a student');
      return;
    }
    if (!form.report_type || !form.academic_year || !form.term) {
      setError('Report Type, Academic Year, and Term are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const finalStatus = targetStatus || form.status || 'draft';
      const payload = {
        student_id: parseInt(form.student_id),
        report_type: form.report_type,
        academic_year: form.academic_year,
        term: form.term,
        age: form.age ? parseInt(form.age) : null,
        status: finalStatus,
        strengths: form.strengths,
        goals: form.goals,
        teacher_comment: form.teacher_comment,
        assessments: form.assessments
      };

      if (modalMode === 'add') {
        await api.post('/admin/student-reports', payload);
        setSuccessMsg('Report created successfully!');
      } else {
        await api.put(`/admin/student-reports/${editingReportId}`, payload);
        setSuccessMsg('Report updated successfully!');
      }

      setModalMode(null);
      loadReports();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save report card');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!deletingReport) return;
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/admin/student-reports/${deletingReport.id}`);
      setSuccessMsg(`Report #${deletingReport.id} deleted successfully.`);
      setDeletingReport(null);
      loadReports();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete report card');
    } finally {
      setDeleting(false);
    }
  };

  // Compute unique Academic Years in Descending order (newest first)
  const defaultYears = ['2027-28', '2026-27', '2025-26', '2024-25', '2023-24'];
  const dbYears = reports.map(r => r.academic_year).filter(Boolean);
  const academicYearsList = Array.from(new Set([...defaultYears, ...dbYears])).sort((a, b) => b.localeCompare(a));

  // Client-side strict filter by selected academic_year
  const filteredReportsByYear = reports.filter(r => {
    if (!filters.academic_year) return true;
    const rYear = String(r.academic_year || '').trim();
    const selYear = String(filters.academic_year).trim();
    const first4 = selYear.substring(0, 4);
    return rYear === selYear || rYear.startsWith(first4) || selYear.startsWith(rYear);
  });

  const sortedReports = sortItems(filteredReportsByYear, sortOption, 'student_name', 'created_at');
  const selectedStudent = availableStudents.find(s => String(s.id) === String(form.student_id));
  const studentDisplayName = selectedStudent ? selectedStudent.full_name : (allStudents.find(s => String(s.id) === String(form.student_id))?.full_name || 'Select Student');

// Helper function to format raw keys cleanly (e.g. montessori_materials -> Montessori Materials)
const formatKeyText = (text) => {
  if (!text) return '';
  let str = String(text).trim();
  
  const customMap = {
    'montessori_materials': 'Montessori Materials',
    'not_yet_presented': 'Not Yet Presented',
    'presented_and_mastered': 'Achieved / Mastered',
    'presented_and_progressing': 'Developing',
    'support_required': 'Support Required',
    'not_yet_observed': 'Not Yet Observed',
    'achieved': 'Achieved',
    'mastered': 'Mastered',
    'developing': 'Developing'
  };

  const lower = str.toLowerCase();
  if (customMap[lower]) return customMap[lower];

  str = str.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ');
  return str.replace(/\b\w/g, c => c.toUpperCase());
};

const optionPills = ['Achieved', 'Developing', 'Support Required', 'Not Yet Presented'];

const getOptionColorStyle = (option, isSelected) => {
  const op = option.toLowerCase();
  if (op.includes('achieved') || op.includes('mastered')) {
    return {
      background: isSelected ? '#059669' : '#ecfdf5',
      color: isSelected ? '#ffffff' : '#047857',
      border: '1.5px solid ' + (isSelected ? '#059669' : '#a7f3d0'),
      boxShadow: isSelected ? '0 4px 10px rgba(5, 150, 105, 0.3)' : 'none'
    };
  }
  if (op.includes('developing') || op.includes('progressing')) {
    return {
      background: isSelected ? '#0284c7' : '#f0f9ff',
      color: isSelected ? '#ffffff' : '#0369a1',
      border: '1.5px solid ' + (isSelected ? '#0284c7' : '#bae6fd'),
      boxShadow: isSelected ? '0 4px 10px rgba(2, 132, 199, 0.3)' : 'none'
    };
  }
  if (op.includes('support') || op.includes('required') || op.includes('emerging')) {
    return {
      background: isSelected ? '#d97706' : '#fffbeb',
      color: isSelected ? '#ffffff' : '#b45309',
      border: '1.5px solid ' + (isSelected ? '#d97706' : '#fde68a'),
      boxShadow: isSelected ? '0 4px 10px rgba(217, 119, 6, 0.3)' : 'none'
    };
  }
  return {
    background: isSelected ? '#64748b' : '#f8fafc',
    color: isSelected ? '#ffffff' : '#475569',
    border: '1.5px solid ' + (isSelected ? '#64748b' : '#cbd5e1'),
    boxShadow: isSelected ? '0 4px 10px rgba(100, 116, 139, 0.3)' : 'none'
  };
};

  return (
    <div>
      <PageHeader
        title="Student Progress Reports"
        subtitle="Developmental milestone tracking, toddler environment, baby environment, and pre-primary reports."
        action={
          <button type="button" className="primary-button" onClick={openAddModal} style={{ whiteSpace: 'nowrap' }}>
            + Add Report
          </button>
        }
      />

      {successMsg && (
        <div style={{ background: '#d1fae5', color: '#065f46', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontWeight: 500 }}>
          {successMsg}
        </div>
      )}
      {error && <div className="error-message">{error}</div>}

      <div className="filter-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        {!isCenterManager && (
          <select value={filters.branch_id} onChange={(e) => setFilters(prev => ({ ...prev, branch_id: e.target.value, class_id: '' }))}>
            <option value="">All branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </select>
        )}

        {(filters.branch_id || isCenterManager) && (
          <select value={filters.class_id} onChange={(e) => setFilters(prev => ({ ...prev, class_id: e.target.value }))}>
            <option value="">All Classes</option>
            {classesList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.section ? ` (${c.section})` : ''}
              </option>
            ))}
          </select>
        )}

        <select value={filters.academic_year} onChange={(e) => setFilters(prev => ({ ...prev, academic_year: e.target.value }))}>
          <option value="">All Academic Years</option>
          {academicYearsList.map(yr => (
            <option key={yr} value={yr}>
              {yr}{yr === currentSessionYear ? ' (Current Session)' : ''}
            </option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Student</th>
              <th>Branch</th>
              <th>Class</th>
              <th>Report Type</th>
              <th>Term</th>
              <th>Academic Year</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: 24 }}>Loading reports...</td>
              </tr>
            ) : sortedReports.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No reports found for this selection.</td>
              </tr>
            ) : (
              sortedReports.map((report) => (
                <tr key={report.id}>
                  <td>{report.id}</td>
                  <td style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate(`/reports/${report.id}`)}>
                    {report.student_name || '-'}
                  </td>
                  <td>{report.branch_name || '-'}</td>
                  <td style={{ fontWeight: 500, color: '#475569' }}>{report.class_name || '-'}</td>
                  <td>{report.report_type || '-'}</td>
                  <td>{report.term}</td>
                  <td>{report.academic_year}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      background: report.status === 'published' ? '#d1fae5' : '#fef3c7',
                      color: report.status === 'published' ? '#065f46' : '#92400e'
                    }}>
                      {report.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        title="View Report"
                        onClick={() => navigate(`/reports/${report.id}`)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 13 }}
                      >
                        👁️
                      </button>
                      <button
                        title="Edit Report"
                        onClick={() => openEditModal(report)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                      >
                        ✏️ Edit
                      </button>

                      {report.status === 'published' ? (
                        <button
                          title="Revert to Draft"
                          disabled={publishingId === report.id}
                          onClick={() => handleQuickPublishToggle(report)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid #fcd34d',
                            background: '#fef3c7',
                            color: '#92400e',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          📝 {publishingId === report.id ? '...' : 'Unpublish'}
                        </button>
                      ) : (
                        <button
                          title="Publish Report"
                          disabled={publishingId === report.id}
                          onClick={() => handleQuickPublishToggle(report)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid #6ee7b7',
                            background: '#d1fae5',
                            color: '#065f46',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          📢 {publishingId === report.id ? '...' : 'Publish'}
                        </button>
                      )}

                      <button
                        title="Delete Report"
                        onClick={() => setDeletingReport(report)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', cursor: 'pointer', fontSize: 13 }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── CREATE / EDIT REPORT MODAL (MOBILE APP DESIGN) ── */}
      {modalMode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16
        }}>
          <div style={{
            background: '#4a148c',
            borderRadius: 24,
            width: '100%',
            maxWidth: 720,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            margin: 'auto',
            boxShadow: '0 25px 50px -12px rgba(74, 20, 140, 0.4)'
          }}>
            
            {/* Header Bar */}
            <div style={{
              background: 'linear-gradient(135deg, #3b0764 0%, #581c87 100%)',
              color: '#ffffff',
              padding: '18px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: 20,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 4
                  }}
                >
                  ←
                </button>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff' }}>
                  {modalMode === 'add' ? 'Fill Assessment Report' : `Edit Assessment Report #${editingReportId}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setModalMode(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  color: '#ffffff',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  fontSize: 16,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div style={{
              background: '#f4f0fb',
              padding: '20px 24px',
              overflowY: 'auto',
              flex: 1
            }}>
              {error && (
                <div className="error-message" style={{ marginBottom: 16, borderRadius: 12 }}>
                  {error}
                </div>
              )}

              {/* Student Info Card (App Style Header) */}
              <div style={{
                background: '#ffffff',
                borderRadius: 16,
                padding: '16px 20px',
                marginBottom: 20,
                boxShadow: '0 4px 14px rgba(88, 28, 135, 0.08)',
                border: '1px solid #ede9fe'
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#3b0764', marginBottom: 4 }}>
                  {studentDisplayName}
                </div>
                <div style={{ fontSize: 13, color: '#6b21a8', fontWeight: 600, opacity: 0.85 }}>
                  {form.report_type} · {form.term} ({form.academic_year}) · Age: {form.age || '5'}
                </div>
              </div>

              {/* Setup Controls (Student Select, Type, Term, Year, Age) */}
              <div style={{
                background: '#ffffff',
                borderRadius: 16,
                padding: 16,
                marginBottom: 20,
                boxShadow: '0 4px 14px rgba(88, 28, 135, 0.05)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12
              }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 4 }}>Select Student *</label>
                  <select
                    disabled={modalMode === 'edit'}
                    value={form.student_id}
                    onChange={(e) => setForm(p => ({ ...p, student_id: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #ddd6fe', fontSize: 13, background: '#faf5ff', color: '#3b0764', fontWeight: 600 }}
                  >
                    <option value="">Select Student</option>
                    {availableStudents.map(s => (
                      <option key={s.id} value={s.id}>{s.full_name} ({s.branch_name || 'Branch ' + s.branch_id})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 4 }}>Environment / Report Type *</label>
                  <select
                    value={form.report_type}
                    onChange={(e) => handleReportTypeChange(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #ddd6fe', fontSize: 13, background: '#faf5ff', color: '#3b0764', fontWeight: 600 }}
                  >
                    <option value="Baby Environment">Baby Environment</option>
                    <option value="Toddler Environment">Toddler Environment</option>
                    <option value="Pre-Primary Environment">Pre-Primary Environment</option>
                    <option value="Custom">Custom Assessment</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 4 }}>Term *</label>
                  <select
                    value={form.term}
                    onChange={(e) => setForm(p => ({ ...p, term: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #ddd6fe', fontSize: 13, background: '#faf5ff', color: '#3b0764', fontWeight: 600 }}
                  >
                    <option value="Term 1">Term 1</option>
                    <option value="Term 2">Term 2</option>
                    <option value="Term 3">Term 3</option>
                    <option value="Final Term">Final Term</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 4 }}>Academic Year *</label>
                  <input
                    type="text"
                    value={form.academic_year}
                    onChange={(e) => setForm(p => ({ ...p, academic_year: e.target.value }))}
                    placeholder="e.g. 2024-25"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #ddd6fe', fontSize: 13, background: '#faf5ff', color: '#3b0764', fontWeight: 600, boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 4 }}>Student Age (Years)</label>
                  <input
                    type="number"
                    value={form.age}
                    onChange={(e) => setForm(p => ({ ...p, age: e.target.value }))}
                    placeholder="e.g. 5"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #ddd6fe', fontSize: 13, background: '#faf5ff', color: '#3b0764', fontWeight: 600, boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Assessment Categories List (Accordions) */}
              {Object.entries(form.assessments || {}).map(([category, items]) => {
                const isCollapsed = !!collapsedCategories[category];
                return (
                  <div
                    key={category}
                    style={{
                      background: '#ffffff',
                      borderRadius: 18,
                      marginBottom: 16,
                      border: '1.5px solid #ede9fe',
                      boxShadow: '0 4px 14px rgba(88, 28, 135, 0.05)',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Category Accordion Header */}
                    <div
                      onClick={() => toggleCategoryCollapse(category)}
                      style={{
                        padding: '16px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: '#ffffff'
                      }}
                    >
                      <h4 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#3b0764' }}>
                        {formatKeyText(category)}
                      </h4>
                      <span style={{ fontSize: 16, color: '#6b21a8', fontWeight: 700, width: 20, textAlign: 'center' }}>
                        {isCollapsed ? '▼' : '▲'}
                      </span>
                    </div>

                    {/* Milestone Items List inside Accordion */}
                    {!isCollapsed && (
                      <div style={{ padding: '0 20px 20px 20px', borderTop: '1px solid #f3e8ff', paddingTop: 16 }}>
                        {Object.keys(items || {}).length === 0 ? (
                          <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
                            No evaluation questions in this category.
                          </div>
                        ) : (
                          Object.entries(items || {}).map(([itemKey, itemVal]) => (
                            <div key={itemKey} style={{ marginBottom: 18 }}>
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#3b0764' }}>
                                  {formatKeyText(itemKey)}
                                </div>
                              </div>

                              {/* Option Pills (Achieved, Developing, Support Required, Not Yet Presented) */}
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {optionPills.map(option => {
                                  const isSelected = String(itemVal).toLowerCase() === option.toLowerCase();
                                  const styleDetails = getOptionColorStyle(option, isSelected);
                                  return (
                                    <button
                                      key={option}
                                      type="button"
                                      onClick={() => handleAssessmentStatusChange(category, itemKey, option)}
                                      style={{
                                        padding: '8px 16px',
                                        borderRadius: 24,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        ...styleDetails
                                      }}
                                    >
                                      {isSelected && <span style={{ fontSize: 14 }}>✓</span>}
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Additional Notes Card */}
              <div style={{
                background: '#ffffff',
                borderRadius: 18,
                padding: 20,
                marginBottom: 20,
                border: '1.5px solid #ede9fe',
                boxShadow: '0 4px 14px rgba(88, 28, 135, 0.05)'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: 17, fontWeight: 800, color: '#3b0764' }}>
                  Additional Notes
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 6 }}>
                      Areas of Strength
                    </label>
                    <textarea
                      rows="3"
                      value={form.strengths}
                      onChange={(e) => setForm(p => ({ ...p, strengths: e.target.value }))}
                      placeholder="Write strengths..."
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: '1.5px solid #ddd6fe',
                        fontSize: 13,
                        background: '#faf5ff',
                        color: '#3b0764',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 6 }}>
                      Next Developmental Goals
                    </label>
                    <textarea
                      rows="3"
                      value={form.goals}
                      onChange={(e) => setForm(p => ({ ...p, goals: e.target.value }))}
                      placeholder="Write next goals..."
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: '1.5px solid #ddd6fe',
                        fontSize: 13,
                        background: '#faf5ff',
                        color: '#3b0764',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: '#581c87', display: 'block', marginBottom: 6 }}>
                      Teacher Comment
                    </label>
                    <textarea
                      rows="3"
                      value={form.teacher_comment}
                      onChange={(e) => setForm(p => ({ ...p, teacher_comment: e.target.value }))}
                      placeholder="Write comments..."
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: '1.5px solid #ddd6fe',
                        fontSize: 13,
                        background: '#faf5ff',
                        color: '#3b0764',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Bottom Action Bar (App Style Sticky Bottom Bar) */}
            <div style={{
              background: '#3b0764',
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <button
                type="button"
                onClick={() => handleSaveReport('draft')}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '13px 20px',
                  borderRadius: 30,
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'transparent',
                  color: '#ffffff',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              <button
                type="button"
                onClick={() => handleSaveReport('published')}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '13px 20px',
                  borderRadius: 30,
                  border: 'none',
                  background: '#ffffff',
                  color: '#3b0764',
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease'
                }}
              >
                {saving ? 'Publishing...' : 'Publish Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRMATION MODAL ── */}
      {deletingReport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 420, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Delete Report Card?</h3>
            <p style={{ color: '#475569', fontSize: 14 }}>
              Are you sure you want to delete report <strong>#{deletingReport.id}</strong> for student <strong>{deletingReport.student_name}</strong>?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setDeletingReport(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteReport}
                disabled={deleting}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
